/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 23-09-2026
 *
 * Het wortelcomponent van de applicatie (sinds golf 4e): niets dan de router-outlet en drie dingen die
 * over alle schermen heen gaan — de toetsafhandeling die uit `app/app.js` komt, de testingang op
 * `window.dlTest` in een development-build, en het meelezen met de sessie van de andere tabbladen.
 *
 * De testingang is er voor het testharnas in de devrepo: de browsertestreeksen spreken de applicatie
 * daarlangs aan. Tot golf 4f was dat de migratie-interface op `window._libs` (plan-golf-4f.md, G3 en B4).
 */
import {Component, HostListener, inject, isDevMode} from '@angular/core';
import {Router, RouterOutlet} from '@angular/router';
import {DlTestingangService} from './testingang/dl-testingang.service';
import {ApiService} from './backend/api.service';
import {OpslagService} from './model/opslag.service';
import {SessieService} from '../services/sessie.service';

@Component({
    selector: 'dl-app',
    imports: [RouterOutlet],
    template: '<router-outlet></router-outlet>',
    styles: [':host { display: block; height: 100%; }']
})
export class App {

    /** Of er nog een authkey is; die staat in `localStorage` en is dus van alle tabbladen samen. */
    private readonly api = inject(ApiService);

    /** De sessie in dit tabblad, die na het wegvallen van de authkey opgeruimd moet worden. */
    private readonly sessie = inject(SessieService);

    /** De opslagketen: een geplande save mag niet meer vertrekken zonder sessie. */
    private readonly opslag = inject(OpslagService);

    /** Om naar het inlogscherm te gaan. */
    private readonly router = inject(Router);

    constructor() {
        //alleen in een development-build; een productiebuild maakt de testingang nooit aan
        if (isDevMode()) {
            (window as unknown as {dlTest?: DlTestingangService}).dlTest = inject(DlTestingangService);
        }
    }

    /**
     * Haalt dit tabblad naar het inlogscherm zodra de sessie in een ánder tabblad beëindigd is.
     *
     * De authkey staat in `localStorage` (`authPersistence: "storage"`) en is daarmee van alle
     * tabbladen samen: logt er één uit, dan zijn ze allemaal uitgelogd. In dit tabblad draait er op dat
     * moment geen regel code — het `storage`-event is het enige signaal dat we krijgen. Zonder deze
     * afhandeling blijft het bouwscherm staan alsof er niets aan de hand is, tot de eerstvolgende call
     * op een ongeldige sessie stukloopt. Overgenomen uit ATRPlan (`app.component.ts`), waar dezelfde
     * opzet zit.
     *
     * `heeftAuth` leest de opslag rechtstreeks, dus dat is meteen de waarheid; het signaal
     * `SessieService.ingelogd()` staat in het geheugen van dít tabblad en weet van niets. Alleen het
     * bouwscherm hoeft weg: op het inlogscherm, het wachtwoordlinkscherm en de opstartroutes is het
     * normaal dat er geen authkey is. Het `storage`-event vuurt bij elke wijziging in een ander
     * tabblad — ook van de splitterverdeling of het bewaarde domein — dus de eerste vraag is of het
     * hier eigenlijk wel over gaat.
     */
    @HostListener('window:storage')
    protected controleerSessie(): void {
        if (this.api.heeftAuth || !this.router.url.startsWith('/build')) {
            return;
        }
        //dezelfde opruiming als bij het uitloggen zelf: een geplande save annuleren (die zou zonder
        //sessie een fout in de console geven) en de sessiegegevens weg
        void this.opslag.nieuw(true);
        this.sessie.logout();
        void this.router.navigate(['/']);
    }

    /**
     * Houdt backspace en delete tegen als ze anders terug zouden navigeren. Uit `app/app.js`; de vorm is
     * dezelfde, alleen hangt de luisteraar nu aan dit component in plaats van aan het document via
     * jQuery. In een tekstveld dat ingevuld mag worden doen de toetsen gewoon hun werk.
     */
    @HostListener('document:keydown', ['$event'])
    protected houdTerugToetsTegen(gebeurtenis: KeyboardEvent): void {
        if (gebeurtenis.key !== 'Backspace' && gebeurtenis.key !== 'Delete') {
            return;
        }
        const doel = gebeurtenis.target as HTMLInputElement | HTMLTextAreaElement | null;
        const soort = (doel as HTMLInputElement)?.type?.toUpperCase() ?? '';
        const inTekstveld = doel?.tagName === 'TEXTAREA'
            || (doel?.tagName === 'INPUT'
                && ['TEXT', 'PASSWORD', 'FILE', 'SEARCH', 'EMAIL', 'NUMBER', 'DATE'].includes(soort));
        if (!inTekstveld || doel?.readOnly || doel?.disabled) {
            gebeurtenis.preventDefault();
        }
    }
}
